import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faAward,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  limit,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components/common/NotificationContext";
import {
  SectionSMMDetail,
  SectionRundown,
} from "@mmfestival/shared-components";
import axios from "axios";
import Iframe from "react-iframe";
import * as SMMData from "@mmfestival/shared-components/data/SMMDetail";

// Sponsor logos scrolling across the white bar that closes the hero. Files live
// in packages/main-frontend/public/smmsponsors/ - add or remove entries here to
// change the strip. Extensions are case-sensitive on the server, so they are
// written exactly as the files are named.
const SPONSOR_SLOTS = [
  { id: "antisep", src: "/smmsponsors/antisep.png" },
  { id: "balenta", src: "/smmsponsors/Balenta.png", size: "h-12 md:h-14" },
  { id: "bobocabin", src: "/smmsponsors/Bobocabin.png" },
  { id: "counterpain", src: "/smmsponsors/counterpain.png" },
  // `size` overrides the default height for logos whose artwork sits small
  // inside a square canvas and would otherwise read as smaller than the rest.
  { id: "eiger", src: "/smmsponsors/eiger.PNG", size: "h-12 md:h-14" },
  { id: "estuaria", src: "/smmsponsors/estuaria.jpg", size: "h-12 md:h-14" },
  { id: "fitbar", src: "/smmsponsors/fitbar.png" },
  { id: "hayalaya", src: "/smmsponsors/hayalaya.png", size: "h-12 md:h-14" },
  { id: "isoplus", src: "/smmsponsors/isoplus.jpg" },
  { id: "kalderacamp", src: "/smmsponsors/kalderacamp.png", size: "h-12 md:h-14" },
  { id: "kopina", src: "/smmsponsors/kopina.png" },
  { id: "malika", src: "/smmsponsors/malika.jpeg" },
  { id: "narmada", src: "/smmsponsors/narmada.png" },
  { id: "ody", src: "/smmsponsors/ody.png", size: "h-12 md:h-14" },
  { id: "paxel", src: "/smmsponsors/paxel.png" },
  { id: "peslun", src: "/smmsponsors/peslun.jpg" },
  { id: "reosen", src: "/smmsponsors/reosen.png" },
  { id: "selaparang", src: "/smmsponsors/selaparang.png", size: "h-12 md:h-14" },
  { id: "suron", src: "/smmsponsors/suron.png" },
  { id: "ukp", src: "/smmsponsors/ukp.PNG", size: "h-12 md:h-14" },
  { id: "unlockindonesia", src: "/smmsponsors/unlockindonesia.png" },
];

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const formatDate = (dateString) => {
  if (!dateString) return "Date not specified";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Date not specified";
  } catch {
    return "Date not specified";
  }
};

// translate status labels (works with Indonesian and English)
const getStatusKey = (status) => {
  if (!status) return "other";
  const s = String(status).trim().toLowerCase();

  // specific mappings (ID/EN)
  if (
    s === "segera dibuka" ||
    s === "segera" ||
    s === "coming soon" ||
    s === "segera dibuka."
  )
    return "coming_soon";

  if (
    s === "registrasi dibuka" ||
    s === "pendaftaran dibuka" ||
    s === "dibuka" ||
    s === "open" ||
    s === "registration open"
  )
    return "open";

  if (
    s === "registrasi ditutup" ||
    s === "pendaftaran ditutup" ||
    s === "ditutup" ||
    s === "closed" ||
    s === "registration closed"
  )
    return "closed";

  // heuristics
  if (s.includes("segera") || s.includes("coming") || s.includes("soon"))
    return "coming_soon";
  if (s.includes("dibuka") || s.includes("open")) return "open";
  if (s.includes("ditutup") || s.includes("tutup") || s.includes("closed"))
    return "closed";

  return "other";
};

const translateStatus = (status) => {
  const key = getStatusKey(status);
  if (key === "open") return "Registration Open";
  if (key === "closed") return "Registration Closed";
  if (key === "coming_soon") return "Coming Soon";
  return "Registration TBD";
};

// MAPPING EVENT ID KE COLLECTION NAME
const EVENT_COLLECTION_MAP = {
  SMM: "pendaftaranSMM",
  JMM: "pendaftaranJMM",
  BMM: "pendaftaranBMM",
};

// --- HARDCODED PREVIEW MEDIA (HYBRID) ---
// Media berikut selalu dipakai oleh komponen (override nilai di Firestore)
const HARD_PREVIEW = {
  image: "/bg/lombok.jpg",
  currentImage: "/bg/lombok.jpg",
  logo: "/logo/SMM.png",
  logodetail: "/logo/SMM1.png",
};

export const SMM = React.memo(() => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [viewMode, setViewMode] = useState("map");
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);
  const isEntryListEnabled = false;

  // === FUNGSI UNTUK GET COLLECTION NAME DARI MAPPING ===
  const getCollectionName = useCallback((eventId) => {
    if (EVENT_COLLECTION_MAP[eventId]) {
      return EVENT_COLLECTION_MAP[eventId];
    }
    return null;
  }, []);

  const checkRegistration = useCallback(
    async (collectionName, eventId) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };
      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId),
          limit(1)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const docRef = querySnapshot.docs[0];
          const data = docRef.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;

          if (
            (status === "settlement" &&
              (paymentType === "free" || harga === 0)) ||
            (status === "pending" && paymentType !== "free") ||
            (status === "settlement" &&
              paymentType !== "free" &&
              data.snapToken)
          ) {
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);

            if (status === "pending" && data.snapToken && data.orderId) {
              try {
                const response = await axios.get(
                  `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                    data.orderId
                  }`,
                  { params: { eventId } }
                );
                const updatedStatus = response.data.transaction_status;
                if (updatedStatus !== status) {
                  if (
                    updatedStatus === "pending" ||
                    updatedStatus === "settlement"
                  ) {
                    await updateDoc(docRef.ref, {
                      transaction_status: updatedStatus,
                    });
                    setTransactionStatus(updatedStatus);
                    return {
                      isRegistered: true,
                      status: updatedStatus,
                      token: data.snapToken,
                    };
                  } else {
                    await deleteDoc(docRef.ref);
                    setRegistrationStatus("not_registered");
                    setTransactionStatus(null);
                    setSnapToken(null);
                    return { isRegistered: false, status: null, token: null };
                  }
                }
              } catch (err) {
                console.error("Error checking payment status:", err);
              }
            }
            return { isRegistered: true, status, token: data.snapToken };
          } else {
            await deleteDoc(docRef.ref);
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            return { isRegistered: false, status: null, token: null };
          }
        } else {
          setRegistrationStatus("not_registered");
          return { isRegistered: false, status: null, token: null };
        }
      } catch (err) {
        console.error("Error checking registration:", err);
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user]
  );

  const fetchEventFromTrilogy = useCallback(() => {
    const found = TrilogyDetail.find((e) => e.id === "SMM");
    if (!found) return null;

    return {
      id: found.id,
      slug: found.slug,
      title: found.title,
      type: found.type,
      status: found.status,
      location: found.location,
      // don't trust TrilogyDetail's image/logo for media — override with HARD_PREVIEW
      image: HARD_PREVIEW.image,
      currentImage: HARD_PREVIEW.currentImage,
      logo: HARD_PREVIEW.logo || null,
      logodetail: HARD_PREVIEW.logodetail || null,
      aboutEvent: found.aboutEvent,
      websiteUrl: found.websiteUrl,
      eventId: found.eventId,
      price: found.price || 0,

      categories: found.categories.map((c) => ({
        categoryName: c.categoryName,
        cot: c.cot,
        date: c.date,
        description: c.description,
        distance: c.distance,
        elevationGain: c.elevationGain,
        price: c.price,
        foreignerPrice: c.foreignerPrice ?? null,
        time: c.eventDateTimestamp
          ? c.eventDateTimestamp.split(" at ")[1]?.split(" UTC")[0] || ""
          : "",
        gpxFileUrl: c.gpxFileUrl || "",
        mapUrl: c.mapUrl || "",
      })),
    };
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", "SMM"));
      if (eventDoc.exists()) {
        const data = eventDoc.data() || {};
        // build eventData from Firestore but OVERRIDE media with hardcoded preview
        const eventData = {
          id: eventDoc.id,
          slug: data.slug,
          title: data.title,
          type: data.type,
          status: data.status,
          location: data.location,
          image: HARD_PREVIEW.image,
          currentImage: HARD_PREVIEW.currentImage,
          logo: HARD_PREVIEW.logo || null,
          logodetail: HARD_PREVIEW.logodetail || null,
          aboutEvent: data.aboutEvent,
          websiteUrl: data.websiteUrl,
          eventId: data.eventId,
          price: data.price || 0,
          categories: Array.isArray(data.categories)
            ? data.categories.map((c) => ({
                categoryName: c.categoryName,
                cot: c.cot,
                date: c.date,
                description: c.description,
                distance: c.distance,
                elevationGain: c.elevationGain,
                price: c.price,
                foreignerPrice: c.foreignerPrice ?? null,
                time: c.eventDateTimestamp
                  ? c.eventDateTimestamp.split(" at ")[1]?.split(" UTC")[0] ||
                    ""
                  : c.time || "",
                gpxFileUrl: c.gpxFileUrl || "",
                mapUrl: c.mapUrl || "",
              }))
            : [],
        };
        setEvent(eventData);
        setLoading(false);
        return eventData;
      }
    } catch (err) {
      console.info("Firebase did not find SMM. Using TrilogyDetail.", err);
    }

    // fallback to TrilogyDetail (but still override media)
    const fallbackEvent = fetchEventFromTrilogy();
    if (fallbackEvent) {
      setEvent(fallbackEvent);
      setLoading(false);
      return fallbackEvent;
    } else {
      setLoading(false);
      return null;
    }
  }, [fetchEventFromTrilogy]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const eventData = await fetchEventData();
      if (!eventData || !mounted) return;

      if (eventData.eventId) {
        try {
          const collName = getCollectionName(eventData.eventId);

          if (mounted) {
            setCollectionName(collName);

            if (collName && user) {
              const { isRegistered, status, token } = await checkRegistration(
                collName,
                eventData.eventId
              );
              setRegistrationStatus(
                isRegistered ? "registered" : "not_registered"
              );
              setTransactionStatus(status);
              setSnapToken(token);
            } else if (!collName && user) {
              setRegistrationStatus("not_registered");
            }
          }
        } catch (err) {
          console.error("Error in fetchData:", err);
          if (mounted) setCollectionName(null);
        }
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [fetchEventData, getCollectionName, checkRegistration, user]);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (!isSnapReady || !window.snap) {
      showNotification("error", "Payment gateway not ready.");
      return;
    }
    if (!snapToken) {
      showNotification("error", "Payment token not found.");
      return;
    }
    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Payment successful!");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/pesanansaya");
        },
        onPending: async () => {
          showNotification("warning", "Payment is processing.");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/pesanansaya");
        },
        onError: () => {
          showNotification("error", "Payment failed.");
        },
        onClose: () => {},
      });
    } catch (err) {
      console.error("Error in payment:", err);
      showNotification("error", "Failed to process payment.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(
    (category) => {
      if (
        !event ||
        !event.title ||
        (category.price == null && event.price == null) ||
        !event.eventId
      ) {
        showNotification("error", "Event data incomplete.");
        return;
      }
      if (!user) {
        showNotification("error", "Please log in first.");
        navigate("/login");
        return;
      }
      if (!collectionName) {
        showNotification("error", "Failed to load event data.");
        console.error("collectionName is null:", { eventId: event.eventId });
        return;
      }
      const title =
        event.type === "seminar"
          ? event.title
          : `${event.title} - ${category.categoryName || ""}`;
      const path =
        event.type === "seminar"
          ? `/formseminar/${event.slug}`
          : `/form/${event.slug}`;
      if (registrationStatus === "registered") {
        if (transactionStatus === "settlement")
          showNotification(
            "info",
            "You are already registered for this event."
          );
        else if (transactionStatus === "pending") handleBayar();
        return;
      }
      const price = category.price ?? event.price ?? 0;
      const foreignerPriceToSend = category.foreignerPrice ?? null;
      navigate(path, {
        state: {
          title,
          price,
          foreignerPrice: foreignerPriceToSend,
          eventId: event.eventId,
        },
      });
    },
    [
      event,
      user,
      collectionName,
      registrationStatus,
      transactionStatus,
      showNotification,
      navigate,
      handleBayar,
    ]
  );

  const handleExportGpx = useCallback(
    debounce(async (gpxFileUrl, selectedCategory, event) => {
      if (!gpxFileUrl) {
        showNotification("error", "GPX file not available.");
        return;
      }
      try {
        const response = await fetch(gpxFileUrl, { mode: "cors" });
        const gpxText = await response.text();
        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                selectedCategory.distance
              }km.gpx`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Error exporting GPX:", err);
        showNotification("error", "Failed to download GPX file.");
      }
    }, 500),
    [showNotification]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = useMemo(() => {
    if (!event?.categories) return [];
    return event.categories;
  }, [event]);

  const selectedCategory = useMemo(
    () => categories[selectedCategoryIndex] || {},
    [categories, selectedCategoryIndex]
  );

  const isRegistrationOpen = useMemo(
    () => getStatusKey(event?.status) === "open",
    [event]
  );

  const isComingSoon = useMemo(
    () => getStatusKey(event?.status) === "coming_soon" || getStatusKey(event?.status) === "other",
    [event]
  );

  const normalizeToArray = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") return [val];
    return [];
  }, []);

  const qualificationItems = useMemo(() => {
    // 1) Firestore per-category field
    const perCat = normalizeToArray(selectedCategory?.qualification);
    if (perCat.length > 0) return perCat;

    // 2) shared-data per-category mapping (qualificationByCategory)
    const mapped =
      selectedCategory?.categoryName &&
      SMMData.qualificationByCategory?.[selectedCategory.categoryName];
    if (mapped) return normalizeToArray(mapped);

    // 3) global fallback (SMMData.qualification)
    return normalizeToArray(SMMData.qualification);
  }, [selectedCategory, normalizeToArray]);

  const raceInclusionItems = useMemo(() => {
    // 1) Firestore per-category field
    const perCat = normalizeToArray(selectedCategory?.raceInclusions);
    if (perCat.length > 0) return perCat;

    // 2) shared-data per-category mapping (raceInclusionsByCategory)
    const mapped =
      selectedCategory?.categoryName &&
      SMMData.raceInclusionsByCategory?.[selectedCategory.categoryName];
    if (mapped) return normalizeToArray(mapped);

    // 3) global fallback (SMMData.raceInclusions)
    return normalizeToArray(SMMData.raceInclusions);
  }, [selectedCategory, normalizeToArray]);

  const displayStatus = useMemo(() => translateStatus(event?.status), [event]);

  const scrollToContent = () => {
    document
      .querySelector(".detail-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading)
    return (
      <div className="relative w-full h-screen overflow-hidden bg-gray-200">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5" />
      </div>
    );

  if (!event)
    return (
      <section className="w-full min-h-screen py-20 text-center text-slate-600 flex-col justify-center relative bg-white">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-slate-600"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          EVENT
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-slate-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          NOT FOUND
        </motion.p>
        <button
          onClick={() => navigate("/")}
          className="text-slate-600 underline"
        >
          Back to Home
        </button>
      </section>
    );

  // local itemVariants to keep animation consistent with other pages
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.08,
        duration: 0.36,
        ease: "easeInOut",
      },
    }),
  };

  return (
    <>
      {/* HERO HEADER */}
      <section
        className="relative w-full min-h-screen text-black text-center flex flex-col justify-center overflow-hidden"
        style={{
          backgroundImage: `url('${HARD_PREVIEW.image}')`,
          backgroundAttachment: "fixed",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* === ZOOM-IN EFFECT === */}
        <motion.div
          className="absolute inset-0 z-1 pointer-events-none"
          style={{
            backgroundImage: `url('${HARD_PREVIEW.currentImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
          initial={{ scale: 1 }}
          animate={{ scale: 1.25 }}
          transition={{
            duration: 12,
            ease: "easeOut",
          }}
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-600/100 to-gray-600/20 z-5"></div>

        {/* Logo & Title & Description */}
        <div className="relative text-white text-center z-10 flex flex-col justify-center flex-grow">
          <motion.div
            className="py-2 flex justify-center"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            {HARD_PREVIEW.logodetail && (
              <img
                src={HARD_PREVIEW.logodetail}
                alt={`${event?.title} Logo Detail`}
                className="h-24 md:h-48 w-auto object-contain drop-shadow-2xl"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
          </motion.div>

          <motion.h2
            className="text-sm sm:text-lg md:text-lg font-normal text-white max-w-[800px] mx-auto px-4 leading-relaxed"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            {event?.aboutEvent}
          </motion.h2>
        </div>

        {/* White bar closing the hero, sitting directly above the green
            category tabs. Clicking it scrolls on, so it replaces the chevron
            without losing that affordance. */}
        <div
          className="absolute bottom-0 left-0 w-full z-20 bg-white cursor-pointer"
          onClick={scrollToContent}
        >
          <div className="overflow-hidden py-2 md:py-2.5">
            {/* The strip is rendered twice back to back and shifted by exactly
                -50%, so the second copy lands where the first started and the
                loop has no visible seam. Animated with Framer Motion because
                the Tailwind config carries no marquee keyframes. */}
            <motion.div
              className="flex w-max items-center gap-10 md:gap-16"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 45, ease: "linear", repeat: Infinity }}
            >
              {[0, 1].map((copy) => (
                <div
                  key={copy}
                  className="flex shrink-0 items-center gap-10 md:gap-16"
                  aria-hidden={copy === 1}
                >
                  {SPONSOR_SLOTS.map((slot) => (
                    <img
                      key={`${copy}-${slot.id}`}
                      src={slot.src}
                      alt={slot.id}
                      className={`${slot.size || "h-8 md:h-10"} w-auto shrink-0 object-contain`}
                      loading="lazy"
                    />
                  ))}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* DETAIL CONTENT */}
      <section className="detail-content bg-white">
        <div className="w-full">
          {/* CATEGORY TABS */}
          {categories.length > 1 && (
            <div className="md:hidden bg-gray-100 text-base font-normal text-gray-400">
              <div className="flex flex-col divide-y divide-gray-300">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`w-full text-left uppercase px-4 py-4 block transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-green-600 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {categories.length > 1 && (
            <div className="hidden md:flex bg-green-400 text-base font-normal text-white border-b border-green-500">
              <div className="flex w-full">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 border-r border-green-500 last:border-r-0 ${
                      selectedCategoryIndex === index
                        ? "bg-green-600 font-normal text-white"
                        : "hover:bg-green-500"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* MAIN INFO */}
          {(event.type === "trail_running" || event.type === "seminar") && (
            <div>
              <div className="hidden md:block">
                <table className="w-full table-auto divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-20 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-20 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Flag-Off
                      </th>

                      {event.type === "trail_running" && (
                        <>
                          <th className="px-20 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Distance
                          </th>
                          <th className="px-20 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Elevation Gain
                          </th>
                          <th className="px-20 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            COT
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-20 py-4 text-sm text-gray-700">
                        {formatDate(selectedCategory.date)}
                      </td>
                      <td className="px-20 py-4 text-sm text-gray-700">
                        {selectedCategory.time || "-"}
                      </td>

                      {event.type === "trail_running" && (
                        <>
                          <td className="px-20 py-4 text-sm text-gray-700">
                            {selectedCategory.distance != null
                              ? `${selectedCategory.distance} KM`
                              : "-"}
                          </td>
                          <td className="px-20 py-4 text-sm text-gray-700">
                            {selectedCategory.elevationGain != null
                              ? `${selectedCategory.elevationGain} M`
                              : "-"}
                          </td>
                          <td className="px-20 py-4 text-sm text-gray-700">
                            {selectedCategory.cot != null
                              ? `${selectedCategory.cot} Hours`
                              : "-"}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="md:hidden">
                <div className="space-y-4 py-4 bg-white">
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Start Date
                    </div>
                    <div className="text-sm text-gray-700">
                      {formatDate(selectedCategory.date)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Flag-Off
                    </div>
                    <div className="text-sm text-gray-700">
                      {selectedCategory.time || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Location
                    </div>
                    <div className="text-sm text-gray-700">
                      {event.location || "-"}
                    </div>
                  </div>
                  {event.type === "trail_running" && (
                    <>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Distance
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.distance != null
                            ? `${selectedCategory.distance} KM`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Elevation Gain
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.elevationGain != null
                            ? `${selectedCategory.elevationGain} M`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          COT
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.cot != null
                            ? `${selectedCategory.cot} Hours`
                            : "-"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* DESCRIPTION */}
          <div className="py-4 text-slate-900">
            <div className="mx-auto max-w-7xl px-4">
              <p className="mb-4 text-lg leading-relaxed text-gray-700">
                {selectedCategory.description || "No description available."}
              </p>

              {/* Qualification & Race Inclusions */}
              {event.type === "trail_running" &&
                (qualificationItems.length > 0 ||
                  raceInclusionItems.length > 0) && (
                  <motion.div
                    className="bg-white rounded-xl border border-slate-200 p-4 mb-4"
                    initial="hidden"
                    whileInView="visible"
                    variants={itemVariants}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <FontAwesomeIcon
                        icon={faAward}
                        className="text-lg text-green-600"
                      />
                      <h2 className="text-xl lg:text-2xl font-semibold text-gray-900">
                        Qualification & Race Inclusions
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {/* Qualification */}
                      <motion.div
                        className="space-y-4"
                        initial="hidden"
                        whileInView="visible"
                        variants={itemVariants}
                        viewport={{ once: true }}
                      >
                        <h3 className="text-lg font-semibold text-gray-900">
                          Qualification
                        </h3>
                        {SMMData.qualificationIntro && (
                          <p className="text-gray-600 text-sm mb-4">
                            {SMMData.qualificationIntro}
                          </p>
                        )}
                        {qualificationItems.map((q, i) => (
                            <motion.div
                              key={`qual-inline-${i}`}
                              custom={i}
                              initial="hidden"
                              whileInView="visible"
                              variants={itemVariants}
                              viewport={{ once: true }}
                            >
                              <div className="flex gap-4">
                                <div className="flex-shrink-0 mt-1">
                                  <div className="w-2 h-2 rounded-full bg-green-600"></div>
                                </div>
                                <div className="flex-1">
                                  <p className="text-gray-600 text-sm leading-relaxed">
                                    {q}
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                      </motion.div>

                      {/* Race Inclusions */}
                      <motion.div
                        className="space-y-4"
                        initial="hidden"
                        whileInView="visible"
                        variants={itemVariants}
                        viewport={{ once: true }}
                      >
                        <h3 className="text-lg font-semibold text-gray-900">
                          Race Inclusions
                        </h3>
                        <div className="flex flex-wrap gap-3">
                          {raceInclusionItems.map((inc, i) => (
                              <motion.div
                                key={`inc-inline-${i}`}
                                custom={i + 10}
                                initial="hidden"
                                whileInView="visible"
                                variants={itemVariants}
                                viewport={{ once: true }}
                              >
                                <div className="inline-flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-full">
                                  <div className="w-2 h-2 rounded-full bg-green-600 flex-shrink-0" />
                                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap">
                                    {inc}
                                  </span>
                                </div>
                              </motion.div>
                            ))}
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              {event.title === "Pelatihan Tenaga Olahraga Masyarakat" && (
                <div>
                  <h4 className="text-md font-normal text-slate-600 mb-4">
                    RUNDOWN
                  </h4>
                  <SectionRundown
                    rundown={null}
                    formatDate={formatDate}
                    eventTitle={event.title}
                  />
                </div>
              )}
            </div>
          </div>


          {/* STATUS / PRICE / PARTICIPANTS / NAVIGATION */}
          <div className="px-4 py-4">
            <div className="mx-auto max-w-7xl">
              {/* DESKTOP WRAPPER */}
              <div className="hidden md:block md:px-4">
                {/* STATUS / PRICE / PARTICIPANTS / GPX FILE - DESKTOP */}
                <div className="border border-gray-300 rounded-2xl p-4">
                  <div className="grid grid-cols-5 gap-4">
                    {/* Status */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Status
                      </p>
                      <div className="flex items-center">
                        <div
                          className={`w-2.5 h-2.5 rounded-full mr-2 ${
                            getStatusKey(event.status) === "open"
                              ? "bg-green-500"
                              : getStatusKey(event.status) === "closed"
                              ? "bg-red-500"
                              : "bg-yellow-500"
                          }`}
                        ></div>
                        <span className="text-sm text-gray-700">
                          {displayStatus}
                        </span>
                      </div>
                    </div>

                    {/* Domestic Price */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Domestic Price
                      </p>
                      <div className="text-sm font-semibold text-green-600">
                        {(() => {
                          const price = selectedCategory?.price ?? event?.price ?? 0;
                          return price > 0 ? `Rp ${price.toLocaleString("id-ID")}` : "Free";
                        })()}
                      </div>
                    </div>

                    {/* Foreigner Price */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        Foreigner Price
                      </p>
                      <div className="text-sm font-semibold text-green-600">
                        {(() => {
                          const price = selectedCategory?.foreignerPrice ?? null;
                          return price ? `Rp ${price.toLocaleString("id-ID")}` : "N/A";
                        })()}
                      </div>
                    </div>

                    {/* Participants */}
                    {event.eventId && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                          Participants
                        </p>
                        <button
                          onClick={() => {
                            if (!isEntryListEnabled) return;
                            navigate(
                              `/entrylist/${event.eventId}/${
                                selectedCategory.distance || "all"
                              }`
                            );
                          }}
                          disabled={!isEntryListEnabled}
                          aria-disabled={!isEntryListEnabled}
                          className={`text-sm transition-colors ${
                            isEntryListEnabled
                              ? "text-green-600 hover:text-green-700 underline"
                              : "text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          Entry List
                        </button>
                      </div>
                    )}

                    {/* GPX FILE */}
                    <div>
                      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                        GPX FILE
                      </p>
                      {selectedCategory.gpxFileUrl &&
                      event.id !== "SMM" &&
                      event.type === "trail_running" ? (
                        <button
                          className="text-green-600 font-medium flex items-center space-x-2 hover:text-green-700 transition-colors"
                          onClick={() =>
                            handleExportGpx(
                              selectedCategory.gpxFileUrl,
                              selectedCategory,
                              event
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faFileDownload} />
                          <span>Download</span>
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Not available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* MOBILE */}
              <div className="md:hidden border border-gray-300 rounded-2xl overflow-hidden">
                {/* Status */}
                <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Status
                  </p>
                  <div className="flex items-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mr-2 ${
                        getStatusKey(event.status) === "open"
                          ? "bg-green-500"
                          : getStatusKey(event.status) === "closed"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-700">
                      {displayStatus}
                    </span>
                  </div>
                </div>

                {/* Domestic Price - Mobile */}
                <div className="px-4 py-3 border-b border-gray-300 bg-white">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Domestic Price
                  </p>
                  <div className="text-sm font-semibold text-green-600">
                    {(() => {
                      const price = selectedCategory?.price ?? event?.price ?? 0;
                      return price > 0 ? `Rp ${price.toLocaleString("id-ID")}` : "Free";
                    })()}
                  </div>
                </div>

                {/* Foreigner Price - Mobile */}
                <div className="px-4 py-3 border-b border-gray-300 bg-white">
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                    Foreigner Price
                  </p>
                  <div className="text-sm font-semibold text-green-600">
                    {(() => {
                      const price = selectedCategory?.foreignerPrice ?? null;
                      return price ? `Rp ${price.toLocaleString("id-ID")}` : "N/A";
                    })()}
                  </div>
                </div>

                {/* Participants */}
                {event.eventId && (
                  <div className="px-4 py-3 border-b border-gray-300 bg-gray-50">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">
                      Participants
                    </p>
                    <button
                      onClick={() => {
                        if (!isEntryListEnabled) return;
                        navigate(
                          `/entrylist/${event.eventId}/${
                            selectedCategory.distance || "all"
                          }`
                        );
                      }}
                      disabled={!isEntryListEnabled}
                      aria-disabled={!isEntryListEnabled}
                      className={`text-sm transition-colors ${
                        isEntryListEnabled
                          ? "text-green-600 hover:text-green-700 underline"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      Entry List
                    </button>
                  </div>
                )}


              </div>
            </div>
          </div>

          {/* EVENT DESCRIPTION */}
          <div className="text-slate-900">
            <div className="mx-auto max-w-7xl px-4">
              <p className="mb-4 text-lg leading-relaxed text-gray-700">
                {event.aboutEvent || "No event description available."}
              </p>
            </div>
          </div>

          {event.type === "trail_running" && (
            <SectionSMMDetail selectedCategory={selectedCategory} />
          )}

          {/* REGISTER BUTTON */}
          <div className="mt-4">
            <div className="hidden md:block">
              <div className="flex items-center justify-end bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex justify-end space-x-0 divide-x divide-gray-300">
                  {registrationStatus === "registered" &&
                  transactionStatus === "settlement" ? (
                    <span className="flex-1 text-center uppercase px-20 py-4 bg-green-600 text-white whitespace-nowrap">
                      YOU ARE REGISTERED
                    </span>
                  ) : registrationStatus === "registered" &&
                    transactionStatus === "pending" ? (
                    <button
                      onClick={handleBayar}
                      className="flex-1 text-center uppercase px-20 py-4 transition-colors duration-300 bg-green-600 font-normal text-white whitespace-nowrap hover:bg-green-800"
                      disabled={!isSnapReady || !snapToken}
                    >
                      COMPLETE PAYMENT
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRegister(selectedCategory)}
                      className={`flex-1 text-center uppercase px-20 py-4 transition-colors duration-300 whitespace-nowrap ${
                        isRegistrationOpen
                          ? "bg-green-600 font-normal text-white hover:bg-green-800"
                          : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                      }`}
                      disabled={!isRegistrationOpen}
                    >
                      {isComingSoon ? "COMING SOON" : isRegistrationOpen ? "REGISTER THIS EVENT" : "REGISTRATION CLOSED"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="md:hidden">
              <div className="bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex flex-col divide-y divide-gray-300">
                  <div>
                    {registrationStatus === "registered" &&
                    transactionStatus === "settlement" ? (
                      <span className="w-full text-center uppercase px-4 py-4 bg-green-600 text-white block transition-colors duration-300 whitespace-nowrap">
                        YOU ARE REGISTERED
                      </span>
                    ) : registrationStatus === "registered" &&
                      transactionStatus === "pending" ? (
                      <button
                        onClick={handleBayar}
                        className="w-full text-center uppercase px-4 py-4 block transition-colors duration-300 bg-green-600 font-normal text-white whitespace-nowrap hover:bg-green-800"
                        disabled={!isSnapReady || !snapToken}
                      >
                        COMPLETE PAYMENT
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRegister(selectedCategory)}
                        className={`w-full text-center uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-green-600 font-normal text-white hover:bg-green-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        {isComingSoon ? "COMING SOON" : isRegistrationOpen ? "REGISTER THIS EVENT" : "REGISTRATION CLOSED"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
});

export default SMM;
